import React from 'react';
import { Layout, Menu, Typography, Space, Button, Tag, Drawer, Grid } from 'antd';
import { Outlet, useLocation } from 'react-router-dom';
import {
    MenuOutlined,
    UserOutlined,
    DownOutlined,
} from '@ant-design/icons';
import { APP_NAME } from '../constants/appConstants';
import { getUser } from '../utils/auth';
import { displayRoleName, ROLE_TAG_COLOR } from '../utils/role';
import {
    buildAdminMenuItems,
    resolveMenuSelectedKey,
    resolveOpenGroupKeys,
} from '../config/adminMenu';
import ProfileEditDrawer from '../components/UserProfile/ProfileEditDrawer';
import SiteFooter from '../components/SiteFooter/SiteFooter';
import './AdminLayout.css';

const { Header, Sider, Content, Footer } = Layout;
const { Text } = Typography;
const { useBreakpoint } = Grid;

class AdminLayout extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            collapsed: false,
            profileOpen: false,
            mobileMenuOpen: false,
            openKeys: [],
        };
    }

    componentDidMount() {
        this.syncOpenKeys();
    }

    componentDidUpdate(prevProps) {
        const prevPath = prevProps.location?.pathname;
        const nextPath = this.props.location?.pathname;
        if (prevPath !== nextPath) {
            this.syncOpenKeys();
        }
    }

    syncOpenKeys = () => {
        const path = this.props.location?.pathname ?? window.location.pathname;
        this.setState({ openKeys: resolveOpenGroupKeys(path) });
    };

    getSelectedKey = () => {
        const path = this.props.location?.pathname ?? window.location.pathname;
        return resolveMenuSelectedKey(path);
    };

    buildMenuItems = () => buildAdminMenuItems(getUser());

    closeMobileMenu = () => {
        if (this.state.mobileMenuOpen) {
            this.setState({ mobileMenuOpen: false });
        }
    };

    handleMenuClick = ({ key }) => {
        this.closeMobileMenu();
        if (key === 'logout' && this.props.onLogout) {
            this.props.onLogout();
        }
    };

    handleOpenChange = (openKeys) => {
        this.setState({ openKeys });
    };

    renderMenu = (theme = 'dark') => {
        const selectedKey = this.getSelectedKey();
        const { openKeys } = this.state;
        return (
            <Menu
                theme={theme}
                selectedKeys={[selectedKey]}
                openKeys={openKeys}
                onOpenChange={this.handleOpenChange}
                mode="inline"
                items={this.buildMenuItems()}
                onClick={this.handleMenuClick}
            />
        );
    };

    render() {
        const { onLogout, isMobile } = this.props;
        const { collapsed, profileOpen, mobileMenuOpen } = this.state;
        const user = getUser();

        return (
            <Layout className="admin-layout">
                {!isMobile && (
                    <Sider
                        collapsible
                        collapsed={collapsed}
                        onCollapse={(value) => this.setState({ collapsed: value })}
                        className="admin-sider"
                        width={220}
                    >
                        <div className="admin-logo">
                            {!collapsed && <span>{APP_NAME}</span>}
                            {collapsed && <span>万</span>}
                        </div>
                        {this.renderMenu('dark')}
                    </Sider>
                )}
                <Layout>
                    <Header className="admin-header">
                        <div className="admin-header__left">
                            {isMobile && (
                                <Button
                                    type="text"
                                    className="admin-menu-trigger"
                                    icon={<MenuOutlined />}
                                    onClick={() => this.setState({ mobileMenuOpen: true })}
                                    aria-label="打开菜单"
                                />
                            )}
                            <Text type="secondary" className="admin-header__title">
                                {APP_NAME}
                            </Text>
                        </div>
                        <Space size="middle" className="admin-header__right">
                            <button
                                type="button"
                                className="admin-account-btn"
                                onClick={() => this.setState({ profileOpen: true })}
                            >
                                <UserOutlined />
                                <span className="admin-account-btn__name">{user?.loginId || '—'}</span>
                                {user?.roleKey && (
                                    <Tag
                                        color={ROLE_TAG_COLOR[user.roleKey]}
                                        className="admin-account-btn__tag"
                                    >
                                        {displayRoleName(user)}
                                    </Tag>
                                )}
                                <DownOutlined className="admin-account-btn__arrow" />
                            </button>
                            {!isMobile && (
                                <Button type="link" size="small" onClick={onLogout}>
                                    退出
                                </Button>
                            )}
                        </Space>
                    </Header>
                    <Content className="admin-content">
                        <Outlet />
                    </Content>
                    <Footer className="admin-footer">
                        <SiteFooter variant="light" />
                    </Footer>
                </Layout>

                {isMobile && (
                    <Drawer
                        title="导航菜单"
                        placement="left"
                        width="78%"
                        open={mobileMenuOpen}
                        onClose={this.closeMobileMenu}
                        className="admin-mobile-drawer"
                        styles={{ body: { padding: 0 } }}
                    >
                        <div className="admin-mobile-drawer__logo">{APP_NAME}</div>
                        {this.renderMenu('light')}
                    </Drawer>
                )}

                <ProfileEditDrawer
                    open={profileOpen}
                    onClose={() => this.setState({ profileOpen: false })}
                    isMobile={isMobile}
                />
            </Layout>
        );
    }
}

/** 注入 location 与断点，子路由切换时布局会重渲染并更新 Menu 选中态 */
function AdminLayoutWithLocation(props) {
    const location = useLocation();
    const screens = useBreakpoint();
    const isMobile = !screens.lg;
    return <AdminLayout {...props} location={location} isMobile={isMobile} />;
}

export default AdminLayoutWithLocation;
